import { ApiError, formatMoney, queryKeys, type Unit } from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronDown, ChevronRight, Warehouse, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { AiAssistButton } from '../components/ai-assist.js'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable, SortHeaderButton } from '../components/data-table.js'
import { usePaymentLookups } from '../components/record-payment-dialog.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface StockLevel {
  productId: string
  productName: string
  sku: string
  locationId: string
  locationName: string
  qty: number
  reserved: number
  available: number
  reorderPoint: number | null
  effectiveReorderPoint: number
}

interface Movement {
  id: string
  productName: string
  locationName: string
  qtyDelta: number
  reason: string
  /** The donor device's IMEI, set only on a salvage movement. */
  refImei: string | null
  actorLabel: string | null
  note: string | null
  createdAt: string
}

const REASON_LABEL: Record<string, string> = {
  receive: 'Received',
  sale: 'Sold',
  return: 'Returned',
  adjustment: 'Adjusted',
  transfer_in: 'Transferred in',
  transfer_out: 'Transferred out',
  damage: 'Damaged',
  salvage: 'Salvaged',
  office_use: 'Office use',
  component: 'Fitted to device',
  vendor_return: 'Returned to vendor',
}

interface Location {
  id: string
  key: string
  name: string
  isActive: boolean
}

interface ProductGroup {
  productId: string
  productName: string
  sku: string
  reorderPoint: number | null
  effectiveReorderPoint: number
  locations: StockLevel[]
  /** Totals across every location — what the collapsed parent row shows and sorts on. */
  totalQty: number
  totalReserved: number
  totalAvailable: number
  /** What the Location cell reads, so sorting by it matches what is on screen. */
  locationLabel: string
}

/** One row per product, carrying the cross-location totals the parent row shows. */
function groupLevelsByProduct(levels: StockLevel[]): ProductGroup[] {
  const groups = new Map<
    string,
    Omit<ProductGroup, 'totalQty' | 'totalReserved' | 'totalAvailable' | 'locationLabel'>
  >()
  for (const level of levels) {
    const group = groups.get(level.productId)
    if (group) {
      group.locations.push(level)
    } else {
      groups.set(level.productId, {
        productId: level.productId,
        productName: level.productName,
        sku: level.sku,
        reorderPoint: level.reorderPoint,
        effectiveReorderPoint: level.effectiveReorderPoint,
        locations: [level],
      })
    }
  }
  return [...groups.values()].map((group) => ({
    ...group,
    totalQty: group.locations.reduce((sum, l) => sum + l.qty, 0),
    totalReserved: group.locations.reduce((sum, l) => sum + l.reserved, 0),
    totalAvailable: group.locations.reduce((sum, l) => sum + l.available, 0),
    locationLabel:
      group.locations.length > 1
        ? `${group.locations.length} locations`
        : (group.locations[0]?.locationName ?? ''),
  }))
}

type LevelSortKey = 'product' | 'location' | 'qty' | 'reserved' | 'available'
interface LevelSort {
  key: LevelSortKey
  order: 'asc' | 'desc'
}

function compareGroups(a: ProductGroup, b: ProductGroup, key: LevelSortKey): number {
  switch (key) {
    case 'product':
      return a.productName.localeCompare(b.productName)
    case 'location':
      return a.locationLabel.localeCompare(b.locationLabel)
    case 'qty':
      return a.totalQty - b.totalQty
    case 'reserved':
      return a.totalReserved - b.totalReserved
    case 'available':
      return a.totalAvailable - b.totalAvailable
  }
}

function compareLevels(a: StockLevel, b: StockLevel, key: LevelSortKey): number {
  switch (key) {
    // A product's own locations have nothing to tell apart by product, so
    // sorting by Product leaves them under it in location order.
    case 'product':
    case 'location':
      return a.locationName.localeCompare(b.locationName)
    case 'qty':
      return a.qty - b.qty
    case 'reserved':
      return a.reserved - b.reserved
    case 'available':
      return a.available - b.available
  }
}

/**
 * Sorts the products, and each product's expanded locations by the same
 * column — the whole table is here in memory (levels are accessories, a few
 * hundred rows at most), so this is a local sort rather than a round trip.
 * Product name breaks ties so equal numbers never shuffle between renders.
 */
function sortProductGroups(groups: ProductGroup[], { key, order }: LevelSort): ProductGroup[] {
  const direction = order === 'asc' ? 1 : -1
  return groups
    .map((group) => ({
      ...group,
      locations: [...group.locations].sort((a, b) => direction * compareLevels(a, b, key)),
    }))
    .sort(
      (a, b) =>
        direction * compareGroups(a, b, key) ||
        a.productName.localeCompare(b.productName) ||
        a.sku.localeCompare(b.sku),
    )
}

function isLow(available: number, effectiveReorderPoint: number): boolean {
  return available <= effectiveReorderPoint
}

/**
 * Shows the effective threshold on the badge itself — a product with no
 * reorder point of its own is judged against the store default, and that
 * needs to be visible, not just correct.
 */
function AvailableCell({
  available,
  effectiveReorderPoint,
  isStoreDefault,
}: {
  available: number
  effectiveReorderPoint: number
  isStoreDefault: boolean
}) {
  const low = isLow(available, effectiveReorderPoint)
  return (
    <span className={`tabular font-medium ${low ? 'text-warning' : ''}`}>
      {available}
      {low ? (
        <Badge
          tone="warning"
          className="ms-2"
          title={`At or below ${effectiveReorderPoint} (${isStoreDefault ? 'store default' : 'reorder point'})`}
        >
          Low
        </Badge>
      ) : null}
    </span>
  )
}

function ProductCell({
  productName,
  sku,
  expandable,
  expanded,
  onToggle,
}: {
  productName: string
  sku: string
  expandable: boolean
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="flex items-center gap-1.5">
      {expandable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} locations for ${productName}`}
          className="text-foreground-muted hover:text-foreground"
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
      ) : (
        <span className="inline-block size-3.5" />
      )}
      <div>
        <p className="font-medium">{productName}</p>
        <p className="font-identifier text-xs text-foreground-muted">{sku}</p>
      </div>
    </div>
  )
}

const LEVELS_TH_CLASS =
  'px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted'

function LevelsHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string
  sortKey: LevelSortKey
  sort: LevelSort
  onSort: (next: LevelSort) => void
}) {
  const active = sort.key === sortKey
  return (
    <th
      className={LEVELS_TH_CLASS}
      aria-sort={active ? (sort.order === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <SortHeaderButton
        active={active}
        order={sort.order}
        onClick={() =>
          onSort({ key: sortKey, order: active && sort.order === 'asc' ? 'desc' : 'asc' })
        }
      >
        {label}
      </SortHeaderButton>
    </th>
  )
}

export function StockPage() {
  const { can } = useAuth()
  const navigate = useNavigate()
  // Not schema-validated: this route only ever receives these from the
  // "View stock levels" / "Receive stock" actions on a product row, which
  // set them all themselves.
  const search = useSearch({ strict: false }) as {
    productId?: string
    sku?: string
    receive?: boolean
  }
  const [adjusting, setAdjusting] = useState<StockLevel | null>(null)
  const [transferring, setTransferring] = useState<StockLevel | null>(null)
  const [receiving, setReceiving] = useState(false)
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<LevelSort>({ key: 'product', order: 'asc' })
  const [lowOnly, setLowOnly] = useState(false)

  // A product-row link can land here already knowing the product: open the
  // receive dialog and/or expand that product's per-location rows — once, on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: act on the link once, on mount
  useEffect(() => {
    if (search.receive) setReceiving(true)
    if (search.productId) setExpandedProducts(new Set([search.productId]))
  }, [])

  const { data: levels, isLoading } = useQuery({
    queryKey: queryKeys.stock.levels({}),
    queryFn: () => api.get<StockLevel[]>('/stock'),
  })

  const allGroups = useMemo(
    () => sortProductGroups(groupLevelsByProduct(levels ?? []), sort),
    [levels, sort],
  )
  const productGroups = useMemo(
    () =>
      search.productId
        ? allGroups.filter((group) => group.productId === search.productId)
        : allGroups,
    [allGroups, search.productId],
  )
  // "Low" is a product's total against its effective reorder point — its own
  // if it has one, otherwise the store default — the same test the badge
  // makes. A column sort alone can't answer it, because 12 available against
  // a reorder point of 15 is low and 3 against a default of 3 is too.
  const lowGroups = useMemo(
    () => productGroups.filter((group) => isLow(group.totalAvailable, group.effectiveReorderPoint)),
    [productGroups],
  )
  const visibleGroups = lowOnly ? lowGroups : productGroups

  function toggleExpanded(productId: string) {
    setExpandedProducts((current) => {
      const next = new Set(current)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  const { data: movements, isLoading: movementsLoading } = useQuery({
    queryKey: queryKeys.stock.movements({ limit: 100 }),
    queryFn: () =>
      api.get<{ items: Movement[]; nextCursor: string | null }>('/stock/movements', {
        query: { limit: 100 },
      }),
  })

  const canActOnStock = can('inventory:adjust') || can('inventory:write') || can('orders:write')

  const movementColumns = useMemo<ColumnDef<Movement, unknown>[]>(
    () => [
      {
        header: 'When',
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-foreground-muted tabular">
            {new Date(row.original.createdAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      { header: 'Product', accessorKey: 'productName' },
      {
        header: 'Change',
        accessorKey: 'qtyDelta',
        cell: ({ row }) => (
          <span
            className={`tabular font-medium ${row.original.qtyDelta > 0 ? 'text-success' : 'text-danger'}`}
          >
            {row.original.qtyDelta > 0 ? '+' : ''}
            {row.original.qtyDelta}
          </span>
        ),
      },
      {
        header: 'Reason',
        accessorKey: 'reason',
        cell: ({ row }) => (
          <div>
            <Badge>{REASON_LABEL[row.original.reason] ?? row.original.reason}</Badge>
            {row.original.refImei ? (
              <p className="mt-0.5 font-identifier text-2xs text-foreground-muted">
                from {row.original.refImei}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        header: 'By',
        accessorKey: 'actorLabel',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {row.original.actorLabel ?? 'System'}
          </span>
        ),
      },
      {
        header: 'Note',
        accessorKey: 'note',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">{row.original.note ?? '—'}</span>
        ),
      },
    ],
    [],
  )

  return (
    <Page>
      <PageHeader
        title="Stock"
        description="Accessory quantities and the ledger behind them. Every change is recorded."
        actions={
          can('inventory:adjust') ? (
            <Button variant="primary" onClick={() => setReceiving(true)}>
              Receive new stock
            </Button>
          ) : null
        }
      />

      <Tabs defaultValue="levels">
        <TabsList>
          <TabsTrigger value="levels">Levels</TabsTrigger>
          <TabsTrigger value="movements">Movement history</TabsTrigger>
        </TabsList>

        <TabsContent value="levels">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-foreground-muted">
              Phones aren't counted here — each handset is tracked individually by IMEI in{' '}
              <Link to="/provisioning" className="text-primary hover:underline">
                Provisioning
              </Link>
              . This list is the quantity-tracked accessories.
            </p>
            <div className="flex shrink-0 items-center gap-3">
              {search.productId ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-2.5 py-1 text-xs text-foreground-muted">
                  Showing {allGroups.find((g) => g.productId === search.productId)?.productName ?? search.sku}
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/stock', search: {} })}
                    aria-label="Clear product filter"
                    className="text-foreground-muted hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              ) : null}
              <label className="flex items-center gap-2 text-xs text-foreground-muted">
                <Switch checked={lowOnly} onCheckedChange={setLowOnly} />
                Low stock only
                <span className="tabular">({lowGroups.length})</span>
              </label>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-sunken/60">
                    <LevelsHeader label="Product" sortKey="product" sort={sort} onSort={setSort} />
                    <LevelsHeader
                      label="Location"
                      sortKey="location"
                      sort={sort}
                      onSort={setSort}
                    />
                    <LevelsHeader label="On hand" sortKey="qty" sort={sort} onSort={setSort} />
                    <LevelsHeader
                      label="Reserved"
                      sortKey="reserved"
                      sort={sort}
                      onSort={setSort}
                    />
                    <LevelsHeader
                      label="Available"
                      sortKey="available"
                      sort={sort}
                      onSort={setSort}
                    />
                    {canActOnStock ? <th className={LEVELS_TH_CLASS} /> : null}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 8 }, (_, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-border last:border-0">
                        {Array.from({ length: 5 + (canActOnStock ? 1 : 0) }, (_, columnIndex) => (
                          <td key={columnIndex} className="px-3 py-2.5">
                            <Skeleton
                              className="h-3.5"
                              style={{ width: columnIndex === 0 ? '60%' : '40%' }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : visibleGroups.length === 0 ? (
                    <tr>
                      <td colSpan={5 + (canActOnStock ? 1 : 0)}>
                        {search.productId ? (
                          <EmptyState
                            icon={Warehouse}
                            variant="no-results"
                            title="No stock records for this product yet"
                            description="Use Receive new stock to give it an opening quantity at a location."
                            onClearFilters={() => navigate({ to: '/stock', search: {} })}
                          />
                        ) : lowOnly ? (
                          <EmptyState
                            icon={Warehouse}
                            variant="no-results"
                            title="Nothing is running low"
                            description="Every product is above its reorder point, or the store default."
                            onClearFilters={() => setLowOnly(false)}
                          />
                        ) : (
                          <EmptyState
                            icon={Warehouse}
                            title="Nothing stocked yet"
                            description="Receive accessories or make an opening adjustment to start."
                          />
                        )}
                      </td>
                    </tr>
                  ) : (
                    visibleGroups.map((group) => {
                      const multiLocation = group.locations.length > 1
                      const expanded = expandedProducts.has(group.productId)
                      // Always defined — groupLevelsByProduct never creates an empty group.
                      const firstLocation = group.locations[0]
                      if (!firstLocation) return []

                      const parentRow = (
                        <tr
                          key={group.productId}
                          className="border-b border-border transition-colors last:border-0"
                        >
                          <td className="px-3 py-2">
                            <ProductCell
                              productName={group.productName}
                              sku={group.sku}
                              expandable={multiLocation}
                              expanded={expanded}
                              onToggle={() => toggleExpanded(group.productId)}
                            />
                          </td>
                          <td className="px-3 py-2 text-foreground-muted">{group.locationLabel}</td>
                          <td className="px-3 py-2 tabular">{group.totalQty}</td>
                          <td className="px-3 py-2 tabular text-foreground-muted">
                            {group.totalReserved}
                          </td>
                          <td className="px-3 py-2">
                            <AvailableCell
                              available={group.totalAvailable}
                              effectiveReorderPoint={group.effectiveReorderPoint}
                              isStoreDefault={group.reorderPoint === null}
                            />
                          </td>
                          {canActOnStock ? (
                            <td className="px-3 py-2">
                              {!multiLocation ? (
                                <div className="flex justify-end gap-1">
                                  {can('orders:write') && firstLocation.available > 0 ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        navigate({
                                          to: '/orders/new',
                                          search: {
                                            addProductId: firstLocation.productId,
                                            fromLocationId: firstLocation.locationId,
                                            fromLocationName: firstLocation.locationName,
                                          },
                                        })
                                      }
                                    >
                                      Sell
                                    </Button>
                                  ) : null}
                                  {can('inventory:write') ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setTransferring(firstLocation)}
                                    >
                                      Transfer
                                    </Button>
                                  ) : null}
                                  {can('inventory:adjust') ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setAdjusting(firstLocation)}
                                    >
                                      Adjust
                                    </Button>
                                  ) : null}
                                </div>
                              ) : null}
                            </td>
                          ) : null}
                        </tr>
                      )

                      const childRows =
                        multiLocation && expanded
                          ? group.locations.map((location) => (
                              <tr
                                key={location.locationId}
                                className="border-b border-border bg-surface-sunken/30 transition-colors last:border-0"
                              >
                                <td className="px-3 py-2" />
                                <td className="ps-8 py-2 text-foreground-muted">
                                  {location.locationName}
                                </td>
                                <td className="px-3 py-2 tabular">{location.qty}</td>
                                <td className="px-3 py-2 tabular text-foreground-muted">
                                  {location.reserved}
                                </td>
                                <td className="px-3 py-2">
                                  {/* "Low" is judged on the product's total, not one shelf, so
                                      only the parent row carries the badge. */}
                                  <span className="tabular font-medium">{location.available}</span>
                                </td>
                                {canActOnStock ? (
                                  <td className="px-3 py-2">
                                    <div className="flex justify-end gap-1">
                                      {can('orders:write') && location.available > 0 ? (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            navigate({
                                              to: '/orders/new',
                                              search: {
                                                addProductId: location.productId,
                                                fromLocationId: location.locationId,
                                                fromLocationName: location.locationName,
                                              },
                                            })
                                          }
                                        >
                                          Sell
                                        </Button>
                                      ) : null}
                                      {can('inventory:write') ? (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setTransferring(location)}
                                        >
                                          Transfer
                                        </Button>
                                      ) : null}
                                      {can('inventory:adjust') ? (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setAdjusting(location)}
                                        >
                                          Adjust
                                        </Button>
                                      ) : null}
                                    </div>
                                  </td>
                                ) : null}
                              </tr>
                            ))
                          : []

                      return [parentRow, ...childRows]
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="movements">
          <p className="mb-3 text-xs text-foreground-muted">
            Every accessory stock change — deliveries, sales, corrections, damage — is recorded here
            with who made it and why. This ledger is append-only: corrections are made by adding an
            entry, never by editing one.
          </p>
          <DataTable
            data={movements?.items ?? []}
            columns={movementColumns}
            isLoading={movementsLoading}
            empty={
              <EmptyState
                icon={Warehouse}
                title="No movements yet"
                description="Every stock change will appear here with who made it and why."
              />
            }
          />
        </TabsContent>
      </Tabs>

      <AdjustDialog level={adjusting} onClose={() => setAdjusting(null)} />
      <TransferDialog level={transferring} onClose={() => setTransferring(null)} />
      <ReceiveStockDialog
        open={receiving}
        onClose={() => {
          setReceiving(false)
          // Drop the receive flag so reopening from the button starts blank,
          // but keep the product filter the link also applied.
          if (search.receive)
            navigate({ to: '/stock', search: { productId: search.productId, sku: search.sku } })
        }}
        initialProductId={search.receive ? search.productId : undefined}
      />
    </Page>
  )
}

/**
 * The scrapped-device picker for a salvage adjustment — same debounced
 * IMEI search over `GET /units` as the vending slot loader
 * (`machine-detail.tsx`'s `SerializedSlotDialog`), scoped to `status:
 * 'scrapped'` instead of `sellable`. No `productId` filter: the donor phone
 * is a different product from the part being stocked.
 */
function ScrappedUnitPicker({
  value,
  onChange,
}: {
  value: Unit | null
  onChange: (unit: Unit | null) => void
}) {
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q), 200)
    return () => clearTimeout(timer)
  }, [q])

  const filters = { status: 'scrapped' as const, q: debouncedQ || undefined }
  const { data: candidates, isFetching: searching } = useQuery({
    queryKey: queryKeys.units.list(filters),
    queryFn: () =>
      api.get<{ items: Unit[] }>('/units', {
        query: { ...filters, sort: 'imei', order: 'asc', pageSize: 50 },
      }),
    enabled: value === null,
  })

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
        <div>
          <span className="font-identifier">{value.imei}</span>
          <span className="ms-2 text-foreground-muted">{value.productName}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by IMEI…"
        autoFocus
      />
      <div className="max-h-40 overflow-y-auto rounded-md border border-border">
        {searching ? (
          <p className="p-2 text-sm text-foreground-muted">Searching…</p>
        ) : (candidates?.items.length ?? 0) === 0 ? (
          <p className="p-2 text-sm text-foreground-muted">
            No scrapped devices match. A device has to be marked scrapped before parts can be
            salvaged from it.
          </p>
        ) : (
          candidates?.items.map((unit) => (
            <button
              key={unit.id}
              type="button"
              onClick={() => onChange(unit)}
              className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-start text-sm hover:bg-surface-sunken"
            >
              <span className="font-identifier">{unit.imei}</span>
              <span className="shrink-0 text-xs text-foreground-muted">{unit.productName}</span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

function AdjustDialog({ level, onClose }: { level: StockLevel | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [delta, setDelta] = useState('')
  const [reason, setReason] = useState('adjustment')
  const [note, setNote] = useState('')
  const [sourceUnit, setSourceUnit] = useState<Unit | null>(null)
  const [expenseCategoryId, setExpenseCategoryId] = useState('')
  const [expenseAmount, setExpenseAmount] = useState('')
  const { can } = useAuth()
  const canExpense = can('expenses:manage')

  // Reset on every open — a cancelled adjustment must not leak its values
  // into the next product's dialog.
  useEffect(() => {
    if (!level) return
    setDelta('')
    setReason('adjustment')
    setNote('')
    setSourceUnit(null)
    setExpenseCategoryId('')
    setExpenseAmount('')
  }, [level])

  const isSalvage = reason === 'salvage'
  // t1223: stock used in the office leaves inventory and is booked as an expense at cost.
  const isOfficeUse = reason === 'office_use'
  const { categories } = usePaymentLookups(level !== null && isOfficeUse)
  const { data: product } = useQuery({
    queryKey: queryKeys.products.detail(level?.productId ?? ''),
    queryFn: () => api.get<{ costCents: number | null }>(`/products/${level?.productId}`),
    enabled: level !== null && isOfficeUse,
  })
  useEffect(() => {
    if (!isOfficeUse || expenseCategoryId) return
    const preferred = categories.find((c) => c.name.trim().toLowerCase() === 'office supplies')
    if (preferred) setExpenseCategoryId(preferred.id)
  }, [isOfficeUse, expenseCategoryId, categories])

  const adjust = useMutation({
    mutationFn: () =>
      api.post('/stock/adjust', {
        productId: level?.productId,
        locationId: level?.locationId,
        qtyDelta: Number.parseInt(delta, 10),
        reason,
        note,
        ...(isSalvage ? { sourceUnitId: sourceUnit?.id } : {}),
        ...(isOfficeUse
          ? {
              expenseCategoryId: expenseCategoryId || undefined,
              expenseAmountCents: expenseOverrideCents || undefined,
            }
          : {}),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() })
      const expenseNumber = (result as { expenseNumber?: string | null } | undefined)
        ?.expenseNumber
      toast.success(
        'Stock adjusted',
        expenseNumber
          ? `Recorded as expense ${expenseNumber}.`
          : 'The change is recorded in the ledger.',
      )
      onClose()
    },
    onError: (error) =>
      toast.error('Could not adjust', error instanceof ApiError ? error.message : undefined),
  })

  const parsedDelta = Number.parseInt(delta, 10)
  const expenseOverrideCents =
    expenseAmount.trim() === '' ? 0 : Math.round(Number(expenseAmount.replace(/[$,\s]/g, '')) * 100)
  const costCents = product?.costCents ?? null
  const expenseCents =
    expenseOverrideCents > 0
      ? expenseOverrideCents
      : costCents && parsedDelta < 0
        ? -parsedDelta * costCents
        : 0
  const valid =
    Number.isInteger(parsedDelta) &&
    parsedDelta !== 0 &&
    note.trim().length >= 3 &&
    (!isSalvage || (parsedDelta > 0 && sourceUnit !== null)) &&
    (!isOfficeUse ||
      (parsedDelta < 0 &&
        expenseCategoryId !== '' &&
        Number.isFinite(expenseOverrideCents) &&
        expenseCents > 0))

  return (
    <Dialog open={level !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust {level?.productName}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            On hand at {level?.locationName}:{' '}
            <span className="tabular font-medium">{level?.qty}</span>
          </p>

          <Field label="Change" required hint="Use a negative number to remove stock.">
            {(props) => (
              <Input
                {...props}
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. -3"
                className="tabular"
                autoFocus
              />
            )}
          </Field>

          <Field label="Reason" required>
            {(props) => (
              <Select
                value={reason}
                onValueChange={(next) => {
                  setReason(next)
                  if (next !== 'salvage') setSourceUnit(null)
                }}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receive">Received</SelectItem>
                  <SelectItem value="adjustment">Count correction</SelectItem>
                  <SelectItem value="damage">Damaged</SelectItem>
                  <SelectItem value="return">Returned</SelectItem>
                  <SelectItem value="salvage">Salvaged from a scrapped device</SelectItem>
                  {canExpense ? (
                    <SelectItem value="office_use">Used in the office (moves cost to expenses)</SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
            )}
          </Field>

          {isOfficeUse ? (
            <div className="space-y-3 rounded-md border border-border p-3">
              <Field label="Expense category" required>
                {(props) => (
                  <Select value={expenseCategoryId || undefined} onValueChange={setExpenseCategoryId}>
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field
                label="Amount to expense"
                hint={
                  costCents
                    ? `Leave blank to use the item's cost (${formatMoney(costCents)} each).`
                    : 'This item has no cost on file — enter the amount.'
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder={expenseCents > 0 ? (expenseCents / 100).toFixed(2) : '0.00'}
                    className="tabular"
                  />
                )}
              </Field>
              {expenseCents > 0 ? (
                <p className="text-sm">
                  <span className="tabular font-medium">{formatMoney(expenseCents)}</span> moves
                  out of inventory and is recorded as a paid expense.
                </p>
              ) : null}
            </div>
          ) : null}

          {isSalvage ? (
            <Field label="Salvaged from" required hint="The scrapped device these parts were pulled from.">
              {() => <ScrappedUnitPicker value={sourceUnit} onChange={setSourceUnit} />}
            </Field>
          ) : null}

          <Field
            label="Explanation"
            required
            hint="Required. An unexplained stock change is not something this system accepts."
          >
            {(props) => (
              <div className="relative">
                <Textarea
                  {...props}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Counted the shelf, three fewer than recorded"
                  className="pe-8"
                />
                <div className="absolute end-1.5 top-1.5">
                  <AiAssistButton
                    target="plain-text"
                    value={note || null}
                    onApply={setNote}
                    context={{
                      fieldLabel: 'stock adjustment explanation',
                      hints: {
                        ...(level?.productName ? { Product: level.productName } : {}),
                        ...(level?.locationName ? { Location: level.locationName } : {}),
                        Reason: reason,
                        ...(Number.isInteger(parsedDelta) ? { Change: String(parsedDelta) } : {}),
                      },
                      maxLength: 500,
                    }}
                  />
                </div>
              </div>
            )}
          </Field>

          {Number.isInteger(parsedDelta) && level ? (
            <p className="text-sm">
              New total: <span className="tabular font-medium">{level.qty + parsedDelta}</span>
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={adjust.isPending}
            onClick={() => adjust.mutate()}
          >
            Record adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Gives a product its first units at a location.
 *
 * "Adjust" only ever appears next to a row that already exists in the Levels
 * table, and a brand-new accessory product has no row to click it on — there
 * was no way to seed one. This is that missing first step: pick any counted
 * product (even one created a minute ago, with nothing in stock anywhere)
 * and a location, and record the opening quantity. It posts to the same
 * /stock/adjust endpoint Adjust uses, which creates the ledger row on first
 * use — there is nothing else to provision.
 */
function ReceiveStockDialog({
  open,
  onClose,
  initialProductId,
}: {
  open: boolean
  onClose: () => void
  initialProductId?: string
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [productId, setProductId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')

  const { data: products } = useQuery({
    queryKey: queryKeys.products.list({ type: 'stock', isActive: true }),
    queryFn: () =>
      api.get<{ items: { id: string; name: string; sku: string }[] }>('/products', {
        query: { type: 'stock', isActive: true, pageSize: 200 },
      }),
  })

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
  })
  const activeLocations = (locations ?? []).filter((l) => l.isActive)
  const selectedProduct = products?.items.find((p) => p.id === productId)
  const selectedLocation = activeLocations.find((l) => l.id === locationId)

  useEffect(() => {
    if (!open) return
    setProductId(initialProductId ?? '')
    setLocationId('')
    setQty('')
    setNote('')
  }, [open, initialProductId])

  const receive = useMutation({
    mutationFn: () =>
      api.post('/stock/adjust', {
        productId,
        locationId,
        qtyDelta: Number.parseInt(qty, 10),
        reason: 'receive',
        note: note.trim() || 'Opening stock',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Stock received', 'The product now has a stock record at that location.')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not receive stock', error instanceof ApiError ? error.message : undefined),
  })

  const parsedQty = Number.parseInt(qty, 10)
  const valid = Boolean(productId) && Boolean(locationId) && Number.isInteger(parsedQty) && parsedQty > 0

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Receive new stock</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Product" required hint="Any counted product — including one with nothing in stock yet.">
            {(props) => (
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a product…" />
                </SelectTrigger>
                <SelectContent>
                  {products?.items.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Location" required>
            {(props) => (
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a location…" />
                </SelectTrigger>
                <SelectContent>
                  {activeLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Quantity" required>
            {(props) => (
              <Input
                {...props}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 50"
                className="tabular"
                autoFocus
              />
            )}
          </Field>

          <Field label="Note" hint="Optional — e.g. a purchase order or shipment reference.">
            {(props) => (
              <div className="relative">
                <Textarea
                  {...props}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="pe-8"
                />
                <div className="absolute end-1.5 top-1.5">
                  <AiAssistButton
                    target="plain-text"
                    value={note || null}
                    onApply={setNote}
                    context={{
                      fieldLabel: 'stock received note',
                      hints: {
                        ...(selectedProduct ? { Product: selectedProduct.name } : {}),
                        ...(selectedLocation ? { Location: selectedLocation.name } : {}),
                        ...(qty ? { Quantity: qty } : {}),
                      },
                      maxLength: 500,
                    }}
                  />
                </div>
              </div>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={receive.isPending}
            onClick={() => receive.mutate()}
          >
            Receive stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Moves accessory stock straight to another location — no request, no
 * waiting for the other side. For asking a location that has to say yes
 * first, or for handsets (tracked individually, not by quantity), staff use
 * a stock request instead.
 */
function TransferDialog({ level, onClose }: { level: StockLevel | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [toLocationId, setToLocationId] = useState('')
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
  })
  const otherLocations = (locations ?? []).filter(
    (l) => l.isActive && l.id !== level?.locationId,
  )
  const destinationLocation = otherLocations.find((l) => l.id === toLocationId)

  useEffect(() => {
    if (!level) return
    setToLocationId('')
    setQty('')
    setNote('')
  }, [level])

  const transfer = useMutation({
    mutationFn: () =>
      api.post('/stock/transfer', {
        productId: level?.productId,
        fromLocationId: level?.locationId,
        toLocationId,
        qty: Number.parseInt(qty, 10),
        note: note.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Stock transferred')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not transfer', error instanceof ApiError ? error.message : undefined),
  })

  const parsedQty = Number.parseInt(qty, 10)
  const valid = Boolean(toLocationId) && Number.isInteger(parsedQty) && parsedQty > 0

  return (
    <Dialog open={level !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer {level?.productName}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            On hand at {level?.locationName}:{' '}
            <span className="tabular font-medium">{level?.qty}</span>
          </p>

          <Field label="Send to" required>
            {(props) => (
              <Select value={toLocationId} onValueChange={setToLocationId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a location…" />
                </SelectTrigger>
                <SelectContent>
                  {otherLocations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Quantity" required>
            {(props) => (
              <Input
                {...props}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="numeric"
                className="tabular"
                autoFocus
              />
            )}
          </Field>

          <Field label="Note">
            {(props) => (
              <div className="relative">
                <Textarea
                  {...props}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="pe-8"
                />
                <div className="absolute end-1.5 top-1.5">
                  <AiAssistButton
                    target="plain-text"
                    value={note || null}
                    onApply={setNote}
                    context={{
                      fieldLabel: 'stock transfer note',
                      hints: {
                        ...(level?.productName ? { Product: level.productName } : {}),
                        ...(level?.locationName ? { From: level.locationName } : {}),
                        ...(destinationLocation ? { To: destinationLocation.name } : {}),
                      },
                      maxLength: 500,
                    }}
                  />
                </div>
              </div>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={transfer.isPending}
            onClick={() => transfer.mutate()}
          >
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export { formatMoney }
